import React, { useState, useEffect } from "react";

import { API, graphqlOperation } from "aws-amplify";
import {
  countInventoryTurns,
  listAccountNames,
} from "../../../graphql/report-queries";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { startOfMonth, sub } from "date-fns";

import { useNotify, TopToolbar } from "react-admin";

import { ForceTitle } from "../../../components/common/ForceTitle";
import { BackButton } from "../../../components/common/BackButton";
import { HelpButton } from "../../../components/common/HelpButton";
import { MonthRangeSlider } from "../../../components/common/MonthRangeSlider";
import SwitchOnOff from "../../../components/common/AntSwitch";
import { logError } from "../../../utils/appUtils";
import { InventoryStatus } from "../../../utils/constants";
import Title from "../Title";

// /reportTurnoverByAccount
const AvgInventoryTurnsByAccount = () => {
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [average, setAverage] = useState(0);
  const [height, setHeight] = useState();
  const [sortBy, setSortBy] = useState("account");
  const [monthRange, setMonthRange] = useState(3);

  useEffect(() => {
    getInventoryTurnsData(monthRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getInventoryTurnsData = async (range) => {
    const monthsToSubtract = range && range > 0 ? range - 1 : 0;
    const startDate = startOfMonth(
      sub(new Date(), { months: monthsToSubtract }),
    );
    let tempData = [];

    try {
      const queryList = [];

      let response = await API.graphql(
        graphqlOperation(listAccountNames, {
          reloadYardId: window.appUser?.reloadYardId,
          activeBusinessName: { beginsWith: { active: "true" } },
        }),
      );
      const accounts = response.data.accountByActiveByName.items;
      setHeight(accounts.length * 50);

      accounts.forEach((account, idx) => {
        tempData[idx] = { name: account.businessName, days: 0, avg: 0 };

        queryList[idx] = API.graphql(
          graphqlOperation(countInventoryTurns, {
            filter: {
              reloadYardId: { eq: window.appUser?.reloadYardId },
              accountId: { eq: account.id },
              status: { ne: InventoryStatus.IN_TRANSIT },
              receivedAt: { gte: startDate },
            },
          }),
        );
      });

      const promiseResults = await Promise.all(queryList);

      promiseResults.forEach((result, idx) => {
        if (result.data.searchInventorys.items?.length > 0) {
          let sum = result.data.searchInventorys.items.reduce(
            (accumulator, item) => {
              return accumulator + item.turnDays;
            },
            0,
          );

          tempData[idx].days = sum / result.data.searchInventorys.total;
        } else tempData[idx].days = 0;
      });

      let cumSum = tempData.reduce((accumulator, item) => {
        return accumulator + item.days;
      }, 0);
      let cumCount = tempData.reduce(
        (total, x) => (x.days > 0 ? total + 1 : total),
        0,
      );
      setAverage(cumSum / cumCount);

      setData(tempData);
    } catch (err) {
      logError("AvgInventoryTurnsByAccount:getInventoryTurnsData", err, notify);
    }
  };

  const handleMonthRangeChangeCommitted = (value) => {
    if (!value) return;
    setMonthRange(value);
    getInventoryTurnsData(value);
  };

  const getSortedData = () => {
    if (!data || data.length === 0) return [];

    const sorted = [...data];

    if (sortBy === "turnover") {
      sorted.sort((a, b) => b.days - a.days);
    } else {
      sorted.sort((a, b) => {
        if (!a.name) return -1;
        if (!b.name) return 1;
        return a.name.localeCompare(b.name);
      });
    }

    return sorted;
  };

  const sortedData = getSortedData();

  return (
    <>
      <div style={{ display: "none" }}>
        <ForceTitle title="Reports" />
      </div>
      <TopToolbar>
        <Title>Avg Inventory Turn Days by Account</Title>
        <HelpButton />
        <BackButton basePath="/reports" />
      </TopToolbar>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: -10,
          marginBottom: -6,
          paddingRight: 24,
        }}
      >
        <div style={{ flex: 1, maxWidth: 360, marginLeft: 270 }}>
          <MonthRangeSlider
            label="Month Range"
            monthRange={monthRange}
            setMonthRange={setMonthRange}
            onChangeCommitted={handleMonthRangeChangeCommitted}
          />
        </div>
        <div>
          <SwitchOnOff
            leftLabel="Account"
            rightLabel="Turnover Days"
            checked={sortBy === "turnover"}
            setChecked={(checked) =>
              setSortBy(checked ? "turnover" : "account")
            }
          />
        </div>
      </div>

      <ResponsiveContainer height={height}>
        <BarChart
          data={sortedData}
          layout="vertical"
          barCategoryGap={6}
          margin={{ top: 5, right: 30, bottom: 5, left: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis
            type="category"
            dataKey="name"
            width={256}
            padding={{ left: 20 }}
          />
          <Tooltip />
          <Bar dataKey="days" stackId="day">
            {sortedData.map((entry) => (
              <Cell
                key={entry.name}
                fill={entry.days > average ? "orange" : "green"}
              />
            ))}
          </Bar>
          <ReferenceLine
            x={average}
            label="Avg"
            stroke="red"
            strokeWidth={6}
            strokeDasharray="6 6"
          />
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

export default AvgInventoryTurnsByAccount;
